import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './Administration.css';
import ActionConfirmation from '../../components/ActionConfirmation';
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  styled,
  List,
  ListItem,
  IconButton,
  Tooltip,
  ListItemIcon
} from '@mui/material';
import {
  countAllUsersByTenant,
  countConnectedUsersByTenant,
  countConnectedGuestByTenant,
  filterUser,
  DeleteUser,
  updateSttModel
} from './store/adminThunks'
import { getTenantSettings } from '../Auth/store/authThunks';
import { getSubDomain } from '../Auth/utils/AuthUtils';
import { patchTenantSttModel } from '../Auth/store/authSlice';
import {
  setConfirmDialogOpen,
  setFliterObj,
  setSelectedUser,
  setUserToDelete,
  setSelectedFilters,
  setSuccessMessage,
  setIsPopupOpen,
  setError,
  setUsers,
  setUsersCount,
} from './store/adminSlice'

import UserForm from "./userForm/UserForm";
import RenameMapDialog from "./RenameMapDialog/RenameMapDialog";
import { useTranslation } from 'react-i18next';
import NotificationManager from '../Notifications/components/NotificationManager/NotificationManager';

import EditIcon from "@mui/icons-material/Edit";

import { getMaps, getMapsByTenant } from '../Map/store/MapDisplayThunks';

export default function Administration() {
  const dispatch = useDispatch();
  const { tenantSettings } = useSelector((state) => state.auth);
  const {
    MapsMinimized
  } = useSelector((state) => state.MapDisplay);
  
  useEffect(() => {
    dispatch(getMapsByTenant());
  }, []);

  const [selectedMap, setSelectedMap] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const handleEditMap = (map) => {
    setSelectedMap(map);
    setOpenEditDialog(true);
  };

  const handleUpdated = (updatedMap) => {
    console.log(updatedMap);
    dispatch(getMapsByTenant());
    setOpenEditDialog(false);
  };

  const PRESET_MODELS = ["voxtral", "kyutai", "whisper", "canary"];
  const rawSttModel = tenantSettings?.stt_model || "voxtral";
  const isWebsocketUrl = rawSttModel.startsWith("ws://") || rawSttModel.startsWith("wss://");
  const isCustom = isWebsocketUrl;
  const normalizedSttModel = isCustom
    ? "custom"
    : (PRESET_MODELS.includes(rawSttModel) ? rawSttModel : "voxtral");
  const [sttModel, setSttModel] = useState(normalizedSttModel);
  const [customUrl, setCustomUrl] = useState(isCustom ? rawSttModel : "");
  const [sttSaveStatus, setSttSaveStatus] = useState(null);

  useEffect(() => {
    const raw = tenantSettings?.stt_model || "voxtral";
    const custom = raw.startsWith("ws://") || raw.startsWith("wss://");
    const norm = custom
      ? "custom"
      : (PRESET_MODELS.includes(raw) ? raw : "voxtral");
    setSttModel(norm);
    if (custom) setCustomUrl(raw);
  }, [tenantSettings?.stt_model]);

  const handleCloseSuccess = useCallback(() => dispatch(setSuccessMessage(null)), [dispatch]);
  const handleCloseError = useCallback(() => dispatch(setError(null)), [dispatch]);

  const handleSaveSttModel = () => {
    const modelToSave = sttModel === "custom" ? customUrl.trim() : sttModel;
    if (sttModel === "custom" && !modelToSave) {
      setSttSaveStatus('error');
      setTimeout(() => setSttSaveStatus(null), 3000);
      return;
    }
    dispatch(updateSttModel({ body: { model: modelToSave } }))
      .unwrap()
      .then(async () => {
        dispatch(patchTenantSttModel(modelToSave));
        const subDomain = getSubDomain();
        if (subDomain) {
          try {
            await dispatch(getTenantSettings({ params: [{ subDomain }] })).unwrap();
          } catch {
            /* le patch ci-dessous / ci-après suffit pour stt_model */
          }
        }
        // L'API publique peut encore renvoyer l'ancien moteur (cache / timing) : on réapplique la valeur enregistrée.
        dispatch(patchTenantSttModel(modelToSave));
        setSttSaveStatus('success');
        setTimeout(() => setSttSaveStatus(null), 3000);
      })
      .catch(() => {
        setSttSaveStatus('error');
        setTimeout(() => setSttSaveStatus(null), 3000);
      });
  };

  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef(null);
  const StyledSelect = styled(Select)(({ theme }) => ({
    backgroundColor: '#FCFCFD',
    borderRadius: 8,
    height: 36,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E3E8EF',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E3E8EF',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E3E8EF',
    },
    '& .MuiSelect-select': {
      paddingTop: 7,
      paddingBottom: 1,
    },
  }));
  const options = ['Connecté', 'Déconnecté', 'Invité'];
  const { t } = useTranslation();

  // 🔵 Style personnalisé du menu (dropdown)
  const menuProps = {
    PaperProps: {
      sx: {
        borderRadius: '1rem',
        minWidth: '12.5rem',
        padding: '0.75rem',
        mt: 1,
        '& .MuiMenuItem-root:not(:last-child)': {
          marginBottom: '1px'
        },
      },
    }
  };
  const {
    isPopupOpen,
    fliterObj,
    users,
    selectedUser,
    userToDelete,
    confirmDialogOpen,
    selectedFilters,
    successMessage,
    usersCount,
    connectedUsersCount,
    connectedGuestCount,
    error
  } = useSelector((state) => state.admin);

  useEffect(() => {
    // Dispatching individually is fine for unrelated counts
    dispatch(countAllUsersByTenant());
    dispatch(countConnectedUsersByTenant());
    dispatch(countConnectedGuestByTenant());
  }, [dispatch]);


  const handleClosePopup = () => {
    dispatch(setIsPopupOpen(false));
    setTimeout(() => {
      dispatch(setSelectedUser(null));
    }, 300); // en ms selon la transition éventuelle
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      dispatch(setError(null));
      try {
        dispatch(filterUser({ body: fliterObj }));
      } catch (err) {
        console.error(err);
        dispatch(setError("Erreur lors de la récupération des utilisateurs.")); // Use dispatch for error
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [fliterObj]);

  const handleChangeFilterText = (event) => {
    const value = event.target.value;

    // Fix 1: Calculate the new filterObj here and dispatch the plain object
    dispatch(setFliterObj({
      ...fliterObj, // Use the current fliterObj from Redux state
      text: value
    }));
  }

  const handleChange = (event) => {
    const selectedValues = event.target.value;
    dispatch(setSelectedFilters(selectedValues));

    // Fix 1: Calculate the new filterObj here and dispatch the plain object
    let newFilterObj = { ...fliterObj }; // Start with a copy of the current filterObj

    const isConnectedSelected = selectedValues?.includes('Connecté');
    const isDisconnectedSelected = selectedValues?.includes('Déconnecté');
    const isGuestSelected = selectedValues?.includes('Invité');

    if (isConnectedSelected && isDisconnectedSelected) {
      delete newFilterObj.socket_id;
    } else if (isConnectedSelected) {
      newFilterObj.socket_id = '!null'; // Representing "not null"
    } else if (isDisconnectedSelected) {
      newFilterObj.socket_id = null;
    } else {
      delete newFilterObj.socket_id;
    }

    if (isGuestSelected) {
      newFilterObj.is_guest = 1;
    } else {
      delete newFilterObj.is_guest;
    }
    dispatch(setFliterObj(newFilterObj));
  };


  const deleteUser = async (email) => {
    try {
      dispatch(DeleteUser({ params: [{ email }] }));

      const currentUsers = users
      const updatedUsers = currentUsers.filter((user) => user.email !== email);
      dispatch(setUsers(updatedUsers)); // ici on passe bien un tableau
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
    }
  };


  const handleDeleteUser = (user) => {
    dispatch(setUserToDelete(user.email));
    dispatch(setConfirmDialogOpen(true));
  }
  const handleUserAdded = (Response, isAdded) => {

    if (isAdded === false) {
      handleClosePopup();
      return;
    }
    if (isAdded === true) {
      const newUser = Response;
      handleClosePopup();
      dispatch(setUsers((prevUsers) => [newUser, ...prevUsers]));
      dispatch(setUsersCount(prev => ({
        nbUsers: (prev?.nbUsers || 0) + 1
      })));

    }

  };
  const handleUserUpdated = () => {
    setLoading(true);
    dispatch(setError(null)); // Use Redux action
    dispatch(filterUser({ body: fliterObj })) // Dispatch the thunk
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
    handleClosePopup(); // Close the popup after dispatching the update
  };

  const getInitialAvatar = (firstName, lastName) => {
    const firstInitial = firstName?.charAt(0)?.toUpperCase() || "";
    const lastInitial = lastName?.charAt(0)?.toUpperCase() || "";
    const initials = firstInitial + lastInitial || "?";

    const colors = [
      "#1abc9c", "#2ecc71", "#3498db", "#9b59b6", "#34495e",
      "#16a085", "#27ae60", "#2980b9", "#8e44ad", "#2c3e50",
      "#f1c40f", "#e67e22", "#e74c3c", "#95a5a6", "#f39c12"
    ];

    const fullName = `${firstName} ${lastName}`;
    const colorIndex = fullName
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;

    const backgroundColor = colors[colorIndex];

    return (
      <div
        className="initial-avatar"
        style={{
          backgroundColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          fontSize: initials.length > 1 ? "1rem" : "1.25rem",
          fontWeight: "bold",
          borderRadius: "50%", // optionnel pour un avatar rond
          width: "2.5rem",
          height: "2.5rem",
          border_radius: "50%",
        }}
      >
        {initials}
      </div>
    );
  };
  return (
    <>
      <div className='administration-container'>
        <div className='container'>

          <div className='title-administration'>
            <div className='title-administration-logo'>
              <img className="people_image" src="/people_map.svg" alt="people map" />
            </div>
            <h3>{t("admin_space")} </h3>
          </div>

          <div className='header-administration'>
            <div className='info-users-box'>
              <h4>
                ({t("total_user_limit")})
              </h4>
              <span className='info-users-value'>{usersCount?.nbUsers} {t("active_accounts")}<span>/ {usersCount?.nombreMaximal} {t("maximum")}</span></span>
            </div>
            <div className='info-users-box'>
              <h4>
                {t("number_of_connected_users")}
              </h4>
              <span className='info-users-value'>{connectedUsersCount} {t("users")} <span>{t("real_time")}</span></span>
            </div>
            <div className='info-users-box'>
              <h4>
                {t("number_of_connected_guests")}
              </h4>
              <span className='info-users-value'>{connectedGuestCount?.nbGuestsConnected} {t("active_guests")}<span> / {connectedGuestCount?.nombreMaximal} {t("maximum")}</span></span>
            </div>
          </div>

          <div className='body-administration'>
            <div className='title-box'>
              <h4>Modèle de transcription</h4>
            </div>
            <div className='stt-model-settings'>
              <p className='stt-model-description'>Choisissez le moteur STT utilisé pour la transcription vocale</p>
              <div className='stt-model-selector'>
                <StyledSelect
                  value={sttModel}
                  onChange={(e) => setSttModel(e.target.value)}
                  MenuProps={menuProps}
                  sx={{ '& .MuiSelect-select': { paddingTop: '7px', paddingBottom: '0.4375rem' } }}
                >
                  <MenuItem value="voxtral">Voxtral (Mistral AI)</MenuItem>
                  <MenuItem value="kyutai">Kyutai (Moshi)</MenuItem>
                  <MenuItem value="whisper">Whisper (Local GPU)</MenuItem>
                  <MenuItem value="canary">Canary 1B v2 (NeMo, GPU)</MenuItem>
                  <MenuItem value="custom">Autre (URL personnalisée)</MenuItem>
                </StyledSelect>
                {sttModel === "custom" && (
                  <input
                    className='stt-custom-url-input'
                    type="text"
                    placeholder="wss://mon-serveur.com/mon-modele"
                    value={customUrl}
                    onChange={(e) => setCustomUrl(e.target.value)}
                  />
                )}
                <button
                  className='primary-button'
                  onClick={handleSaveSttModel}
                >
                  <span className="material-symbols-outlined">save</span>
                  Enregistrer
                </button>
                {sttSaveStatus === 'success' && (
                  <span className='stt-save-feedback stt-save-success'>
                    <span className="material-symbols-outlined">check_circle</span>
                    Enregistré
                  </span>
                )}
                {sttSaveStatus === 'error' && (
                  <span className='stt-save-feedback stt-save-error'>
                    <span className="material-symbols-outlined">error</span>
                    Erreur
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className='body-administration'>
            <div className='title-box'>
              <h4>{t("user_management")} </h4>
              <div class="button-container">
                <button disabled={usersCount?.nbUsers >= usersCount?.nombreMaximal} class='primary-button' onClick={(event) => dispatch(setIsPopupOpen(true))}>

                  {usersCount?.nbUsers < usersCount?.nombreMaximal && (
                    <span class="material-symbols-outlined">
                      person_add
                    </span>)}
                  {usersCount?.nbUsers >= usersCount?.nombreMaximal && (
                    <span class="material-symbols-outlined">
                      person_add_disabled
                    </span>)}
                  {t("add_user")}
                </button>
                {usersCount?.nbUsers >= usersCount?.nombreMaximal && (
                  <span class="tooltip-text">  {t("maximum_number_of_users_reached")}</span>
                )}
              </div>
            </div>
            <div className='filter-users-administration'>
              <input type='text' className='filter-value' placeholder='Rechercher par nom, prénom, email' onChange={handleChangeFilterText} value={fliterObj.text || ''} />
              <StyledSelect
                labelId="multi-select-label"
                multiple
                displayEmpty
                value={selectedFilters}
                onChange={handleChange}

                renderValue={() => (
                  <span className='placeholder-select'>
                    <span class="material-symbols-outlined">
                      tune
                    </span>
                    {t("filter_by")}
                  </span>
                )}
                MenuProps={menuProps}
              >
                {options.map((option, index) => {
                  const isLast = index === options.length - 1;
                  return (
                    <MenuItem
                      key={option}
                      value={option}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        padding: '0px',
                        '&.Mui-selected': {
                          backgroundColor: 'transparent',
                        },
                        '&.MuiMenuItem-root:hover': {
                          backgroundColor: 'transparent',
                        },
                        '&.MuiMenuItem-root': {
                          backgroundColor: 'transparent',
                        },
                        '&.MuiMenuItem-root .MuiTypography-root': {
                          padding: '0.125rem 0.375rem',
                        },
                        '&.Mui-selected .MuiTypography-root': {
                          backgroundColor: '#00c0de',
                          color: 'white',
                          borderRadius: '0.25rem',
                        },
                      }}
                    >
                      <Checkbox
                        checked={selectedFilters.includes(option)}
                        sx={{
                          padding: '0',
                          '& .MuiSvgIcon-root': {
                            borderRadius: '50%',
                          },
                          color: '#00c0de',
                          '&.Mui-checked': {
                            color: '#00c0de',
                          },
                        }}
                      />
                      <ListItemText
                        className={`options-item ${isLast ? 'last' : ''}`}
                        primaryTypographyProps={{
                          sx: {
                            fontFamily: 'Roboto-bold',
                            fontSize: '0.875rem',
                            fontStyle: 'normal',
                            fontWeight: 700,
                            lineHeight: 'normal',
                            marginLeft: '0.625rem',
                            color: index === 0 ? '#097347' : index === 1 ? '#BB241A' : '#9617B2'
                          },
                        }}
                        primary={option}
                      />
                    </MenuItem>
                  );
                })}
              </StyledSelect>
            </div>


            <div className='user-list-administration'>
              {users.map((user) => (
                <div className='user-item' key={user.email}> {/* Add key for list rendering */}
                  <div className='user-ad-infon'>

                    {user?.urlphoto === "Avatar.png" && user?.is_guest === 0 ? (
                      getInitialAvatar(user?.firstName, user?.lastName)
                    ) : (
                      <img
                        src={user?.urlphoto}
                        alt={user?.name?.split(" ")[0] || "avatar"}
                        className="user-photo"
                      />
                    )}

                    <div className='user-name'>
                      <h4>{user?.display_name}
                        {user?.socket_id && <span className='status-tag status-tag-connected'>{t("connected")}</span>}
                        {user?.is_guest === 1 && <span className='status-tag status-tag-guest'>{t("guest")}</span>}
                        {!user?.socket_id && <span className='status-tag status-tag-disconnected'>{t("disconnected")}</span>}
                      </h4>
                      <small>{user?.email}</small>
                    </div>
                  </div>
                  <div className='user-ad-actions'>
                    <button className='edit-ad-user' disabled={user.is_guest === 1} onClick={() => { dispatch(setSelectedUser(user)); dispatch(setIsPopupOpen(true)) }}>
                      <span class="material-symbols-outlined">
                        person_edit
                      </span>
                    </button>
                    <button className='delete-ad-user' onClick={() => handleDeleteUser(user)}>
                      <span class="material-symbols-outlined">
                        delete
                      </span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className='body-administration'>
            <div className='title-box'>
              <h4>Gestion des Maps</h4>
            </div>

            <List className="maps-list">
              {MapsMinimized.map((map) => (
                <ListItem
                  key={map.id}
                  divider
                  secondaryAction={
                    <Tooltip title={t("edit_map_name")}>
                      <IconButton
                        edge="end"
                        onClick={() => handleEditMap(map)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemIcon className="map-list-icon">
                    <img
                      src={map.thumbnail_url}
                      alt="Map"
                      className="map-icon"
                    />
                  </ListItemIcon>

                  <ListItemText primary={map.name} />
                </ListItem>
              ))}
            </List>
          </div>
        </div>
      </div>

      <RenameMapDialog
        open={openEditDialog}
        map={selectedMap}
        onClose={() => setOpenEditDialog(false)}
        onUpdated={handleUpdated}
      />
      <UserForm
        open={isPopupOpen}
        onClose={handleClosePopup}
        selectedUser={selectedUser}
        onUserAdded={handleUserAdded}
        onUserUpdateded={handleUserUpdated}
      />

      <ActionConfirmation
        open={confirmDialogOpen}
        variant="destructive"
        title={'Suppression d’un utilisateur'}
        cancelText={t("cancel")}
        confirmText={t("delete")}
        msg={'Cette action permet de supprimer définitivement l’utilisateur de la liste disponible.Une fois supprimé, cet utilisateur ne pourra plus être sélectionné ni utilisé.'}
        onClose={() => dispatch(setConfirmDialogOpen(false))}
        onConfirm={() => {
          deleteUser(userToDelete);
          dispatch(setConfirmDialogOpen(false))
        }}
      />



    </>)
}